/**
 * Renders an ISO-8601 timestamp as a coarse relative phrase ("2 hours ago").
 *
 * Keep the implementation tiny: this is V1 polish, not a full i18n-aware
 * RelativeTimeFormat consumer. Tooltip carries the absolute ISO string.
 */

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const seconds = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));

  if (seconds < 45) return "just now";
  if (seconds < 90) return "a minute ago";
  if (seconds < HOUR) {
    return `${Math.round(seconds / MINUTE)} minutes ago`;
  }
  if (seconds < 2 * HOUR) return "an hour ago";
  if (seconds < DAY) {
    return `${Math.round(seconds / HOUR)} hours ago`;
  }
  if (seconds < 2 * DAY) return "yesterday";
  if (seconds < WEEK) {
    return `${Math.round(seconds / DAY)} days ago`;
  }
  if (seconds < MONTH) {
    return `${Math.round(seconds / WEEK)} weeks ago`;
  }
  if (seconds < YEAR) {
    return `${Math.round(seconds / MONTH)} months ago`;
  }
  return `${Math.round(seconds / YEAR)} years ago`;
}

export interface RelativeTimeProps {
  iso: string;
}

export function RelativeTime({ iso }: RelativeTimeProps): React.ReactElement {
  return (
    <time
      dateTime={iso}
      title={iso}
      className="text-[11px]"
      style={{ color: "var(--color-hint)" }}
    >
      {formatRelative(iso)}
    </time>
  );
}
